import React, { useState } from 'react';
import { useUser } from '../../context/UserContext';
import { BookOpenIcon, CheckIcon, CalendarIcon, PenIcon, XIcon } from 'lucide-react';
import WritingEditor from '../common/WritingEditor';
const PersonalizedPrompts: React.FC = () => {
  const {
    userData,
    saveWrittenResponse,
    getWrittenResponse
  } = useUser();
  const [activePrompt, setActivePrompt] = useState<number | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState({
    id: '',
    title: '',
    text: ''
  });
  // Generate prompts based on user's human design type, principles, and current garden
  const getPersonalizedPrompts = () => {
    const humanDesignType = userData.humanDesignType || 'Generator';
    const principles = userData.values || [];
    const currentGarden = userData.currentGarden || 'Wisdom';
    // Base prompts by garden
    const gardenPrompts = {
      Wisdom: [{
        id: 'wisdom-reflection',
        title: 'Wisdom Reflection',
        text: "What is one piece of wisdom that you've gained through a challenging experience. How has this wisdom changed how you approach your life?"
      }, {
        id: 'wisdom-question',
        title: 'Question Everything',
        text: 'What question, if you sat with it deeply, might transform your current understanding of yourself or your life?'
      }],
      Love: [{
        id: 'love-compassion',
        title: 'Self-Compassion',
        text: 'Write a letter to yourself from the perspective of unconditional love and acceptance. What do you most need to hear right now?'
      }, {
        id: 'love-forgiveness',
        title: 'Forgiveness Practice',
        text: "Is there someone (including yourself) whom you're struggling to forgive. What might be the first small step toward forgiveness?"
      }],
      Respect: [{
        id: 'respect-boundaries',
        title: 'Boundary Reflection',
        text: 'Reflect on a situation where you need stronger boundaries. What would honoring your needs in this situation look like?'
      }, {
        id: 'respect-listening',
        title: 'Deep Listening',
        text: 'When do you find it most difficult to truly listen to others? What might help you listen more deeply in these moments?'
      }],
      Bravery: [{
        id: 'bravery-fear',
        title: 'Befriending Fear',
        text: 'What fear has been holding you back? If you could dialogue with this fear, what might it be trying to protect, and what would you say to it?'
      }, {
        id: 'bravery-authentic',
        title: 'Authentic Expression',
        text: "In what area of your life do you most long to express yourself more authentically. What's one step you could take in that direction?"
      }],
      Honesty: [{
        id: 'honesty-truth',
        title: 'Your Deeper Truth',
        text: 'What truth have you been avoiding or not fully acknowledging? How might giving voice to this truth serve your growth?'
      }, {
        id: 'honesty-authentic',
        title: 'Authentic Living',
        text: 'Where in your life is there the biggest gap between how you present yourself and how you truly feel? What would help bridge this gap?'
      }],
      Humility: [{
        id: 'humility-learning',
        title: 'Learning Edge',
        text: "What is something you've been wrong about recently. How did recognizing this mistake help you grow?"
      }, {
        id: 'humility-teachers',
        title: 'Unexpected Teachers',
        text: 'Who or what has been your most unexpected teacher recently? What have they helped you understand about yourself?'
      }],
      Truth: [{
        id: 'truth-alignment',
        title: 'Living Your Truth',
        text: 'In what area of your life do you feel most aligned with your deepest truth? What allows this alignment to flourish?'
      }, {
        id: 'truth-intuition',
        title: 'Intuitive Wisdom',
        text: 'Recall a time when you followed your intuition despite doubt or external pressure. What did this experience teach you about trusting yourself?'
      }]
    };
    // Human Design specific prompts
    const designPrompts = {
      Generator: {
        id: 'generator-response',
        title: 'Gut Response',
        text: 'Think of a recent decision you made. Did you follow your gut response? How did that affect the outcome and how you felt about it?'
      },
      'Manifesting Generator': {
        id: 'man-gen-multitasking',
        title: 'Efficient Energy',
        text: "When do you feel most energized by having multiple projects. When does multitasking drain you instead. What's the difference?"
      },
      Projector: {
        id: 'projector-invitation',
        title: 'Waiting for Invitation',
        text: 'Reflect on a time when you waited for the right invitation before taking action. How did this patience serve you?'
      },
      Manifestor: {
        id: 'manifestor-inform',
        title: 'Informing Others',
        text: 'How do you feel about informing others before you take action? When does this practice create more freedom in your relationships?'
      },
      Reflector: {
        id: 'reflector-lunar',
        title: 'Lunar Cycle',
        text: 'How does your energy and clarity shift throughout the lunar cycle? What patterns have you noticed in your decision-making process?'
      }
    };
    // Principle-specific prompts
    const principlePrompts: Record<string, any> = {
      benevolence: {
        id: 'benevolence-kindness',
        title: 'Ripples of Kindness',
        text: "Describe a moment when someone's kindness significantly impacted you. Has this experience influenced how you treat others?"
      },
      'self-direction': {
        id: 'self-direction-authentic',
        title: 'Authentic Choices',
        text: 'When do you feel most authentically yourself? What choices or environments support this authenticity?'
      },
      universalism: {
        id: 'universalism-connection',
        title: 'Human Connection',
        text: 'Describe an experience that helped you recognize your connection to people very different from yourself. What bridges these differences?'
      }
    };
    // Combine prompts based on user data
    const personalPrompts = [];
    // Add garden-specific prompts
    if (gardenPrompts[currentGarden as keyof typeof gardenPrompts]) {
      personalPrompts.push(...gardenPrompts[currentGarden as keyof typeof gardenPrompts]);
    }
    // Add human design prompt if available
    if (humanDesignType && designPrompts[humanDesignType as keyof typeof designPrompts]) {
      personalPrompts.push(designPrompts[humanDesignType as keyof typeof designPrompts]);
    }
    // Add prompts for user's top principles
    principles.slice(0, 2).forEach(principle => {
      if (principlePrompts[principle]) {
        personalPrompts.push(principlePrompts[principle]);
      }
    });
    // Return up to 4 prompts
    return personalPrompts.slice(0, 4);
  };
  const personalizedPrompts = getPersonalizedPrompts();
  const handleOpenEditor = (promptId: string, title: string, text: string) => {
    setCurrentPrompt({
      id: promptId,
      title,
      text
    });
    // Get existing response if available
    const existingResponse = getWrittenResponse(promptId);
    setShowEditor(true);
  };
  const handleSaveResponse = (content: string) => {
    saveWrittenResponse(currentPrompt.id, content, currentPrompt.title, currentPrompt.text);
    setShowEditor(false);
  };
  const isPromptCompleted = (promptId: string) => {
    return !!getWrittenResponse(promptId);
  };
  return <div className="bg-white rounded-xl shadow-md p-6 h-full" data-id="element-215">
      <h2 className="text-xl font-semibold mb-4 flex items-center" data-id="element-216">
        <BookOpenIcon className="mr-2 text-purple-600" size={20} data-id="element-217" />
        Personalized Prompts
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-218">
        Reflective writing prompts tailored to your Human Design type, core
        principles, and current garden.
      </p>
      <div className="space-y-3 mb-4" data-id="element-219">
        {personalizedPrompts.map((prompt, index) => {
        const isCompleted = isPromptCompleted(prompt.id);
        const isActive = activePrompt === index;
        return <div key={prompt.id} className={`border rounded-lg overflow-hidden transition-all ${isCompleted ? 'bg-purple-50 border-purple-200' : 'bg-white border-gray-200'}`} data-id="element-220">
              <div className="p-4 cursor-pointer" onClick={() => setActivePrompt(isActive ? null : index)} data-id="element-221">
                <div className="flex justify-between items-start" data-id="element-222">
                  <div data-id="element-223">
                    <div className="flex items-center" data-id="element-224">
                      <span className={`text-xs px-2 py-0.5 rounded-full mr-2 ${isCompleted ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-225">
                        {isCompleted ? <span className="flex items-center" data-id="element-226">
                            <CheckIcon size={12} className="mr-1" data-id="element-227" />
                            Completed
                          </span> : 'New Prompt'}
                      </span>
                      <h3 className="font-medium" data-id="element-228">{prompt.title}</h3>
                    </div>
                    {isActive && <p className="text-gray-600 mt-2 mb-3" data-id="element-229">{prompt.text}</p>}
                  </div>
                  {!isActive && <div className="text-purple-500" data-id="element-230">
                      {isCompleted ? <CheckIcon size={18} data-id="element-231" /> : <PenIcon size={18} data-id="element-232" />}
                    </div>}
                </div>
                {isActive && <div className="mt-2" data-id="element-233">
                    <button onClick={() => handleOpenEditor(prompt.id, prompt.title, prompt.text)} className="bg-purple-600 text-white py-2 px-4 rounded-lg text-sm hover:bg-purple-700" data-id="element-234">
                      {isCompleted ? 'Edit Response' : 'Write Response'}
                    </button>
                  </div>}
              </div>
            </div>;
      })}
      </div>
      <div className="bg-gray-50 p-4 rounded-lg flex items-center justify-between" data-id="element-235">
        <div data-id="element-236">
          <h3 className="font-medium text-gray-800" data-id="element-237">Weekly Prompts</h3>
          <p className="text-sm text-gray-600" data-id="element-238">
            New prompts arrive every Monday
          </p>
        </div>
        <div className="bg-purple-100 p-2 rounded-full" data-id="element-239">
          <CalendarIcon size={20} className="text-purple-600" data-id="element-240" />
        </div>
      </div>
      {showEditor && <WritingEditor initialContent={getWrittenResponse(currentPrompt.id)?.content || ''} promptTitle={currentPrompt.title} promptText={currentPrompt.text} onSave={handleSaveResponse} onClose={() => setShowEditor(false)} activityId={currentPrompt.id} data-id="element-241" />}
    </div>;
};
export default PersonalizedPrompts;